<template>
  <a-card :bordered="false" class="truelayer-connect-card">
    <div class="truelayer-connect-card__content">
      <div class="truelayer-connect-card__hero">
        <p class="truelayer-connect-card__eyebrow">TrueLayer sandbox workspace</p>
        <h2>Authorize a bank through TrueLayer and retrieve balances with recent statement activity.</h2>
        <p>
          Choose a sandbox provider, complete the hosted redirect, and return to a dedicated statement view without affecting Plaid or Yapily.
        </p>

        <div class="truelayer-connect-card__highlights">
          <span>Sandbox providers</span>
          <span>OAuth redirect callback</span>
          <span>Stored transaction history</span>
        </div>
      </div>

      <div class="truelayer-connect-card__actions">
        <div class="truelayer-connect-card__control-panel">
          <div class="truelayer-connect-card__metric-row">
            <div>
              <span>Accounts</span>
              <strong>{{ accountCount }}</strong>
            </div>
            <div>
              <span>Transactions</span>
              <strong>{{ transactionCount }}</strong>
            </div>
          </div>

          <div class="truelayer-connect-card__selector-block">
            <label class="truelayer-connect-card__selector-label" for="truelayer-provider-select">Provider</label>
            <a-select
              id="truelayer-provider-select"
              show-search
              size="large"
              :value="selectedProviderId || undefined"
              :options="providerOptions"
              :loading="loadingProviders"
              :disabled="loading || loadingProviders || !providers.length"
              option-filter-prop="label"
              placeholder="Select a TrueLayer provider"
              @change="$emit('update:selectedProviderId', $event)"
            />
            <p class="truelayer-connect-card__selector-help">
              Pick a provider that supports accounts, balances, and transactions. Sandbox mock providers are surfaced first when available.
            </p>
          </div>

          <div class="truelayer-connect-card__status-box">
            <a-alert v-if="error" type="error" show-icon :message="error" />
            <a-alert v-else-if="message" type="success" show-icon :message="message" />
            <p v-else>
              Ready to redirect into the TrueLayer consent flow.
            </p>
          </div>

          <a-button
            type="primary"
            size="large"
            class="truelayer-connect-card__button"
            :loading="loading"
            :disabled="loadingProviders || disconnecting || !selectedProviderId"
            @click="$emit('connect')"
          >
            Continue With TrueLayer
          </a-button>

          <a-button
            danger
            size="large"
            class="truelayer-connect-card__button truelayer-connect-card__button--secondary"
            :loading="disconnecting"
            :disabled="loading || !canDisconnect"
            @click="$emit('disconnect')"
          >
            Disconnect TrueLayer
          </a-button>
        </div>
      </div>
    </div>
  </a-card>
</template>

<script>
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'TrueLayerConnect',
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    loadingProviders: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: '',
    },
    message: {
      type: String,
      default: '',
    },
    providers: {
      type: Array,
      default: () => [],
    },
    selectedProviderId: {
      type: String,
      default: '',
    },
    accountCount: {
      type: Number,
      default: 0,
    },
    transactionCount: {
      type: Number,
      default: 0,
    },
    disconnecting: {
      type: Boolean,
      default: false,
    },
    canDisconnect: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['connect', 'disconnect', 'update:selectedProviderId'],
  computed: {
    providerOptions() {
      return this.providers.map((provider) => ({
        value: provider.provider_id,
        label: [
          provider.display_name || provider.provider_id,
          provider.recommended ? 'Recommended' : null,
          provider.recommended_status || null,
        ].filter(Boolean).join(' · '),
      }));
    },
  },
});
</script>

<style scoped>
.truelayer-connect-card {
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 28px;
  background:
    radial-gradient(circle at top left, rgba(255, 255, 255, 0.12), transparent 28%),
    radial-gradient(circle at bottom right, rgba(14, 165, 233, 0.22), transparent 36%),
    linear-gradient(135deg, #111827 0%, #0f3d56 45%, #14532d 100%);
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18);
}

.truelayer-connect-card :deep(.ant-card-body) {
  padding: 34px;
}

.truelayer-connect-card__content {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 380px);
  gap: 28px;
  align-items: stretch;
  color: #f8fafc;
}

.truelayer-connect-card__hero {
  display: grid;
  align-content: start;
}

.truelayer-connect-card__eyebrow {
  margin-bottom: 10px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(226, 232, 240, 0.72);
}

.truelayer-connect-card h2 {
  color: #ffffff;
  margin-bottom: 12px;
  font-size: 34px;
  line-height: 1.15;
}

.truelayer-connect-card__hero > p:last-of-type {
  margin-bottom: 0;
  max-width: 660px;
  font-size: 15px;
  line-height: 1.7;
  color: rgba(226, 232, 240, 0.86);
}

.truelayer-connect-card__highlights {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}

.truelayer-connect-card__highlights span {
  padding: 9px 14px;
  border: 1px solid rgba(226, 232, 240, 0.14);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 600;
}

.truelayer-connect-card__actions {
  display: flex;
}

.truelayer-connect-card__control-panel {
  width: 100%;
  display: grid;
  gap: 18px;
  padding: 22px;
  border: 1px solid rgba(226, 232, 240, 0.14);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
}

.truelayer-connect-card__metric-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.truelayer-connect-card__metric-row div {
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(15, 23, 42, 0.34);
}

.truelayer-connect-card__metric-row span {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(226, 232, 240, 0.68);
}

.truelayer-connect-card__metric-row strong {
  font-size: 24px;
  color: #ffffff;
}

.truelayer-connect-card__status-box {
  min-height: 94px;
  display: grid;
  align-items: center;
}

.truelayer-connect-card__status-box p {
  margin: 0;
  color: rgba(226, 232, 240, 0.84);
  line-height: 1.6;
}

.truelayer-connect-card__selector-block {
  display: grid;
  gap: 10px;
}

.truelayer-connect-card__selector-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(226, 232, 240, 0.72);
}

.truelayer-connect-card__selector-help {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: rgba(226, 232, 240, 0.72);
}

.truelayer-connect-card__selector-block :deep(.ant-select-selector) {
  min-height: 48px;
  border: 1px solid rgba(226, 232, 240, 0.18) !important;
  border-radius: 14px !important;
  background: rgba(15, 23, 42, 0.38) !important;
  box-shadow: none !important;
}

.truelayer-connect-card__selector-block :deep(.ant-select-selection-placeholder),
.truelayer-connect-card__selector-block :deep(.ant-select-selection-item),
.truelayer-connect-card__selector-block :deep(.ant-select-arrow) {
  color: #f8fafc;
}

.truelayer-connect-card__status-box :deep(.ant-alert) {
  border: none;
}

.truelayer-connect-card__button {
  height: 48px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #38bdf8 0%, #16a34a 100%);
  box-shadow: 0 12px 24px rgba(14, 165, 233, 0.24);
}

.truelayer-connect-card__button--secondary {
  background: rgba(255, 255, 255, 0.1);
  box-shadow: none;
}

@media only screen and (max-width: 767px) {
  .truelayer-connect-card__content {
    grid-template-columns: 1fr;
  }

  .truelayer-connect-card h2 {
    font-size: 28px;
  }
}
</style>